import { createContext, useContext, useState, useCallback } from 'react';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbContextValue {
  /** Override map: UUID segment → display label */
  overrides: Map<string, { label: string; path?: string }>;
  /** Full custom trail that replaces URL-derived crumbs entirely */
  trail: BreadcrumbItem[] | null;
  setOverride: (segment: string, label: string, path?: string) => void;
  /** Set a complete breadcrumb trail for the current page */
  setTrail: (trail: BreadcrumbItem[] | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  overrides: new Map(),
  trail: null,
  setOverride: () => {},
  setTrail: () => {},
});

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [overrides, setOverrides] = useState<Map<string, { label: string; path?: string }>>(new Map());
  const [trail, setTrailState] = useState<BreadcrumbItem[] | null>(null);

  const setOverride = useCallback((segment: string, label: string, path?: string) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(segment, { label, path });
      return next;
    });
  }, []);

  const setTrail = useCallback((newTrail: BreadcrumbItem[] | null) => {
    setTrailState(newTrail);
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ overrides, trail, setOverride, setTrail }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  return useContext(BreadcrumbContext);
}
